import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Eye, Check, X, FileText, Image as ImageIcon, Download, Loader2 } from "lucide-react";

export default function Bills() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const r = await api.get("/bills", { params: filter ? { status_f: filter } : {} });
    setItems(r.data);
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <Layout title="Bills" subtitle="Verification"
      action={
        <select className="fin" style={{width:200}} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="bill-filter">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      }>
      <div className="card-sharp">
        <table className="fin">
          <thead><tr><th>Customer</th><th>WhatsApp</th><th>Type</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                <td className="font-medium">{b.customer_name || "—"}</td>
                <td className="text-[#595959] font-mono text-[12px]">{b.customer_wa}</td>
                <td>
                  <span className="flex items-center gap-2 text-[#595959]">
                    {b.media_type === "document" ? <FileText size={14}/> : <ImageIcon size={14}/>}
                    {b.media_type || "image"}
                  </span>
                </td>
                <td className="text-[#595959] text-[12px]">{new Date(b.created_at).toLocaleString()}</td>
                <td>
                  <span className={`badge badge-${b.status}`}>{b.status}</span>
                  {b.ai_decision && b.ai_decision !== "approve" && (
                    <div className="text-[10px] text-[#7A5C0A] mt-1 uppercase tracking-wider" data-testid={`ai-decision-${b.id}`}>
                      AI: {b.ai_decision.replace(/_/g, " ")}
                    </div>
                  )}
                  {b.status === "rejected" && b.verify_notes && (
                    <div className="text-[11px] text-[#8C3A3A] mt-1 italic">“{b.verify_notes}”</div>
                  )}
                </td>
                <td className="text-right">
                  <button className="btn-outline" style={{padding:"6px 10px"}} onClick={() => setPreview(b)} data-testid={`view-bill-${b.id}`}>
                    <Eye size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[#8C8C8C]">No bills.</td></tr>}
          </tbody>
        </table>
      </div>

      {preview && (
        <BillPreviewModal
          bill={preview}
          onClose={() => setPreview(null)}
          onChanged={() => { load(); setPreview(null); }}
        />
      )}
    </Layout>
  );
}

/**
 * Full-featured bill preview modal:
 * - Loads the bill image/PDF as a blob (proper auth via axios interceptor)
 * - Download button
 * - Approve / Reject tabs (available regardless of current status — admin can re-verify)
 * - Rejection reason textbox (required to reject)
 */
export function BillPreviewModal({ bill, onClose, onChanged }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(bill.status === "rejected" ? "reject" : "approve");
  const [reason, setReason] = useState(bill.verify_notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let revoke;
    setLoading(true); setBlobUrl(null); setErr("");
    api.get(`/bills/${bill.id}/image`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "Bill file not found on server." : "Could not load bill."))
      .finally(() => setLoading(false));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [bill.id]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    const ext = bill.media_type === "document" ? "pdf" : "jpg";
    a.download = `bill-${(bill.customer_wa || bill.id).replace(/\D/g, "")}-${bill.id.slice(0, 8)}.${ext}`;
    a.click();
  };

  const submit = async () => {
    setErr(""); setSaving(true);
    try {
      const fd = new FormData();
      fd.append("action", tab);
      if (tab === "reject") {
        if (!reason.trim()) { setErr("Rejection reason is required."); setSaving(false); return; }
        fd.append("notes", reason.trim());
      } else if (reason.trim()) {
        fd.append("notes", reason.trim());
      }
      await api.patch(`/bills/${bill.id}/verify`, fd);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_360px] fade-up" onClick={(e) => e.stopPropagation()}>
        {/* Left: image / pdf */}
        <div className="bg-[#1A1A1A] flex items-center justify-center p-4 lg:p-8 overflow-auto" style={{minHeight: 420}}>
          {loading ? (
            <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : err ? (
            <div className="text-[#F0E4E0] text-sm">{err}</div>
          ) : bill.media_type === "document" ? (
            <iframe src={blobUrl} className="w-full bg-white" style={{height: "75vh"}} title="bill-pdf" />
          ) : (
            <img src={blobUrl} alt="bill" className="max-w-full max-h-[75vh] object-contain bg-white" data-testid="bill-image" />
          )}
        </div>

        {/* Right: actions */}
        <div className="border-l border-[#E6E4E0] flex flex-col overflow-y-auto">
          <div className="p-6 border-b border-[#E6E4E0]">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="label-tiny">Bill Preview</div>
                <div className="text-lg font-light mt-1" style={{fontFamily:"Outfit"}}>{bill.customer_name || "Customer"}</div>
                <div className="font-mono text-[12px] text-[#8C8C8C]">{bill.customer_wa}</div>
                <div className="text-[12px] text-[#8C8C8C] mt-2">{new Date(bill.created_at).toLocaleString()}</div>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`badge badge-${bill.status}`}>{bill.status}</span>
                  <span className="text-[11px] text-[#8C8C8C]">{bill.media_type}</span>
                </div>
              </div>
              <button className="btn-outline" onClick={onClose} data-testid="close-preview-btn">Close</button>
            </div>

            <button className="btn-outline w-full mt-4 flex items-center justify-center gap-2"
                    disabled={!blobUrl} onClick={download} data-testid="download-bill-btn">
              <Download size={14} /> Download
            </button>
          </div>

          {(bill.ai_decision || bill.ai_admin_reason) && (
            <div className="p-6 border-b border-[#E6E4E0] bg-[#FAF8F4]">
              <div className="label-tiny mb-2 flex items-center justify-between">
                <span>AI Validation</span>
                <span className={`text-[10px] px-2 py-0.5 ${
                  bill.ai_decision === "approve" ? "bg-[#E8EFE0] text-[#4A6741]" :
                  bill.ai_decision === "review_suspect" ? "bg-[#FDF5DC] text-[#7A5C0A]" :
                  bill.ai_decision === "unreadable" ? "bg-[#F0E8E0] text-[#7A5C0A]" :
                  "bg-[#F0E4E0] text-[#8C3A3A]"
                }`} data-testid="ai-decision-badge">
                  {bill.ai_decision || "—"}
                </span>
              </div>
              {bill.ai_admin_reason && (
                <div className="text-[12px] text-[#3A3A3A] mb-3 italic leading-relaxed" data-testid="ai-admin-reason">
                  {bill.ai_admin_reason}
                </div>
              )}
              {bill.ai_extracted && (
                <div className="space-y-1.5 text-[12px]">
                  <BillField label="Store" value={bill.ai_extracted.store_name} />
                  <BillField label="Total" value={bill.ai_extracted.total_amount ? `₹${bill.ai_extracted.total_amount}` : ""} />
                  <BillField label="Bill #" value={bill.ai_extracted.bill_number} mono />
                  <BillField label="Bill Date" value={bill.ai_extracted.bill_date} />
                  <BillField label="Mall mentioned" value={bill.ai_extracted.mall_mention} />
                  <BillField label="Sector" value={bill.ai_extracted.sector_mention} />
                  <BillField label="Readability" value={bill.ai_extracted.readability_score != null ? `${bill.ai_extracted.readability_score}/100` : ""} />
                  <BillField label="Confidence" value={bill.ai_extracted.confidence != null ? `${bill.ai_extracted.confidence}/100` : ""} />
                  {bill.ai_extracted.tampering_signs && <BillField label="Tampering" value={bill.ai_extracted.tampering_signs} warn />}
                  {bill.ai_extracted.font_issues && <BillField label="Font issues" value={bill.ai_extracted.font_issues} warn />}
                  {bill.ai_extracted.damage_signs && <BillField label="Damage" value={bill.ai_extracted.damage_signs} warn />}
                  {bill.ai_extracted.totals_consistent === false && <BillField label="Totals match" value="No" warn />}
                </div>
              )}
            </div>
          )}

          <div className="p-6 flex-1">
            <div className="label-tiny mb-3">Verification</div>
            <div className="flex border border-[#E6E4E0]">
              <button
                className={`flex-1 py-2 text-[12px] uppercase tracking-wider font-semibold transition ${tab==="approve" ? "bg-[#4A6741] text-white" : "text-[#4A6741] hover:bg-[#E8EFE0]"}`}
                onClick={() => setTab("approve")} data-testid="tab-approve">
                <Check size={12} className="inline -mt-0.5 mr-1" /> Approve
              </button>
              <button
                className={`flex-1 py-2 text-[12px] uppercase tracking-wider font-semibold transition ${tab==="reject" ? "bg-[#8C3A3A] text-white" : "text-[#8C3A3A] hover:bg-[#F0E4E0]"}`}
                onClick={() => setTab("reject")} data-testid="tab-reject">
                <X size={12} className="inline -mt-0.5 mr-1" /> Reject
              </button>
            </div>

            <div className="mt-4">
              <div className="label-tiny mb-1.5">
                {tab === "reject" ? "Rejection reason (required)" : "Notes (optional)"}
              </div>
              <textarea
                className="fin"
                rows={4}
                placeholder={tab === "reject" ? "e.g. Image unclear, not a Gulshan ONE29 outlet, duplicate bill…" : "Anything to remember about this bill"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="reason-input"
              />
            </div>

            {bill.verified_at && (
              <div className="mt-3 text-[11px] text-[#8C8C8C]">
                Last verified {new Date(bill.verified_at).toLocaleString()}
              </div>
            )}

            {err && <div className="mt-3 text-[13px] text-[#8C3A3A]" data-testid="verify-err">{err}</div>}

            <button
              onClick={submit}
              disabled={saving}
              className={`w-full mt-5 flex items-center justify-center gap-2 px-5 py-3 text-[12px] uppercase tracking-wider font-semibold ${tab==="approve" ? "bg-[#4A6741] text-white" : "bg-[#8C3A3A] text-white"} disabled:opacity-50`}
              data-testid="submit-verify-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {tab === "approve" ? "Approve bill" : "Reject bill"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BillField({ label, value, mono, warn }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#8C8C8C] uppercase tracking-wider text-[10px] w-[88px] shrink-0 pt-0.5">{label}</span>
      <span className={`flex-1 ${mono ? "font-mono text-[11px]" : ""} ${warn ? "text-[#8C3A3A]" : "text-[#1A1A1A]"}`}>{value}</span>
    </div>
  );
}
